import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    Box,
    Heading,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    Spinner,
    Flex,
    Text,
} from '@chakra-ui/react';
import styled from '@emotion/styled/macro';

import { ReactComponent as FileIcon } from '../../icons/file.svg';
import { ReactComponent as FilePaperclipIcon } from '../../icons/file-paperclip.svg';
import { ReactComponent as DownloadIcon } from '../../icons/download.svg';

import { downloadFile, exportItemData } from '../../services/items';

import useCommonToast from '../../hooks/useCommonToast';

const DownloadModal = ({
    item,
    itemId,
    filters,
    itemType,
    templateId,
    basisDataSorting,
    batteryDataSorting,
    headerMessage,
    bodyMessage,
    ...rest
}) => {
    const [isLoading, setLoading] = useState(false);
    const toast = useCommonToast();
    const intl = useIntl();
    const isBatteryItem = itemType === 'battery';

    const exportData = async (type) => {
        setLoading(true);

        if (type === 'template') {
            let url = process.env.PUBLIC_URL + '/' + itemType + '_data_template.xlsx';
            window.open(url, '_blank');
            setLoading(false);

            return;
        }

        let payload = {};

        const dateFrom = filters.toggleDaysFrom ? new Date() : filters.startDateFrom;
        const dateTo = filters.toggleDaysTo ? new Date() : filters.startDateTo;

        if (filters.toggleDaysFrom) {
            dateFrom.setDate(dateFrom.getDate() + parseInt(filters.daysFrom));
        }

        if (filters.toggleDaysTo) {
            dateTo.setDate(dateTo.getDate() + parseInt(filters.daysTo));
        }

        if (itemType === 'basis') {
            const { scalarSorting, dartSpreadSorting } = basisDataSorting;

            payload = {
                scalarFilter: {
                    startDateFrom: dateFrom.toJSON(),
                    startDateTo: dateTo.toJSON(),
                },
                sorting: {
                    orderBy: scalarSorting.sortColumn,
                    orderByDescending: scalarSorting.sortByDescending,
                },
                dartSpreadSortColumn: dartSpreadSorting.sortColumn,
                dartSpreadSortByDescending: dartSpreadSorting.sortByDescending,
            };
        } else if (itemType === 'hedge') {
            payload = filters;
        } else {
            payload = {
                startDateFrom: dateFrom.toJSON(),
                startDateTo: dateTo.toJSON(),
                filterType: parseInt(filters.filterType),
                updateDateFrom: filters.updateDateFrom.toJSON(),
                updateDateTo: filters.updateDateTo.toJSON(),
            };
        }

        const fileInfo = await exportItemData(item, itemId, payload);
        downloadFile(fileInfo);
        setLoading(false);
    };

    const exportBatteryData = async () => {
        setLoading(true);

        try {
            const payload = {
                biddingFilter: {
                    templateId: templateId,
                },
                outageFilter: {
                    sortColumn: batteryDataSorting.outage.sortColumn || null,
                    sortByDescending: batteryDataSorting.outage.sortByDescending || false,
                },
                historicalOutputFilter: {
                    sorting: {
                        orderBy: batteryDataSorting.historicalOutput.sortColumn || null,
                        orderByDescending: batteryDataSorting.historicalOutput.sortByDescending || false,
                        nullsLast: true,
                    },
                },
            };

            const fileInfo = await exportItemData(item, itemId, payload);

            downloadFile(fileInfo);
        } catch (error) {
            if (error.response.status !== 401) {
                const errorMessage = intl.formatMessage({ id: 'common_generic_loading_error' });
                toast({
                    status: 'error',
                    message: errorMessage,
                });
            }
        }

        setLoading(false);
    };

    const headerText = headerMessage || <FormattedMessage id="download_modal_header" />;
    const bodyText = bodyMessage || <FormattedMessage id="download_modal_explanation" />;

    if (isLoading) {
        return (
            <Modal size={'lg'} {...rest}>
                <ModalOverlay />

                <ModalContent pt={3}>
                    <ModalHeader mb={3} pr={12}>
                        <Heading as="h3" variant="h3">
                            {headerText}
                        </Heading>
                    </ModalHeader>
                    <ModalCloseButton mt={4} mr={3} h={6} w={6} />

                    <StyledText>{bodyText}</StyledText>

                    <Flex mb={7} align="center" justify="center">
                        <Spinner />
                    </Flex>
                </ModalContent>
            </Modal>
        );
    }

    return (
        <Modal size={'lg'} {...rest}>
            <ModalOverlay />

            <ModalContent pt={3}>
                <ModalHeader mb={3} pr={12} pl={4}>
                    <Heading as="h3" variant="h3">
                        {headerText}
                    </Heading>
                </ModalHeader>
                <ModalCloseButton mt={4} mr={3} h={6} w={6} />

                <StyledText>{bodyText}</StyledText>

                <OptionWrapper tabIndex="0" as="a" onClick={() => exportData('template')}>
                    <Flex align="center" justify="space-between">
                        <Flex align="center">
                            <CustomFileIcon />
                            <Heading fontWeight={400} ml={2} as="h4" variant="h4">
                                <FormattedMessage id="download_modal_template" />
                            </Heading>
                        </Flex>
                        <DownloadIconWrapper className="download_icon">
                            <DownloadIcon />
                        </DownloadIconWrapper>
                    </Flex>
                </OptionWrapper>

                <OptionWrapper
                    tabIndex="0"
                    as="a"
                    onClick={() => (!isBatteryItem ? exportData('full') : exportBatteryData())}
                >
                    <Flex align="center" justify="space-between">
                        <Flex align="center">
                            <CustomFilePaperclipIcon />
                            <Heading fontWeight={400} ml={2} as="h4" variant="h4">
                                <FormattedMessage id="download_modal_data" />
                            </Heading>
                        </Flex>
                        <DownloadIconWrapper className="download_icon">
                            <DownloadIcon />
                        </DownloadIconWrapper>
                    </Flex>
                </OptionWrapper>
            </ModalContent>
        </Modal>
    );
};

const CustomFileIcon = styled(FileIcon)`
    background: none;
    height: 30px;
    width: auto;

    path {
        fill: var(--chakra-colors-blue-200);
    }
`;

const CustomFilePaperclipIcon = styled(FilePaperclipIcon)`
    background: none;
    height: 30px;
    width: auto;

    path:first-of-type {
        fill: var(--chakra-colors-blue-200);
    }
    path:last-of-type {
        fill: var(--chakra-colors-blue-400);
    }
`;

const OptionWrapper = styled(Box)`
    box-shadow: var(--chakra-shadows-sm);
    border: 1px solid var(--chakra-colors-gray-200);

    padding: 16px 24px;
    margin: 0px 16px 20px 16px;

    &:hover {
        box-shadow: var(--chakra-shadows-md);
        cursor: pointer;

        .download_icon path {
            fill: var(--chakra-colors-blue-400);
        }
    }
`;

const DownloadIconWrapper = styled.div`
    background: var(--chakra-colors-gray-100);
    border-radius: 100%;
    padding: 10px;
    svg {
        height: 18px;
        width: auto;
        border-radius: 100%;
    }
`;

const StyledText = styled(Text)`
    color: var(--chakra-colors-gray-700);
    font-weight: 500;
    margin: 0px 16px 20px 16px;
    font-size: 15px;
`;

export default DownloadModal;
